import { createContext } from "react"

// Display fit of a report page (dashboardCanvas fitPageLayout) and the KPI row baseline.
// The canvas provides it; widgets outside a report page (previews, dialogs) get the empty
// default and keep their own sizing.
//
// - reportNeed(id, px, stops): the frame height a widget's content wants (tables, text), and a
//   table's whole-row stops (widgetContentFit).
// - kpiRows: { [widgetId]: rowKey } for the KPIs on the shown page; KPIs with one key share
//   one value baseline.
// - kpiFits / reportKpiFit(id, { full }): the value size each KPI shows; the row's largest is the
//   baseline strut, so values in a row share a baseline.
/** @type {import("react").Context<{
 * reportNeed?: (id: string, px: number, stops?: number[]) => void,
 * kpiRows?: Record<string, string>,
 * kpiFits?: Record<string, { full: number }>,
 * reportKpiFit?: (id: string, fit: { full: number }) => void,
 * }>} */
export const DashboardFitContext = createContext({})

// Frame height (px) the content of a table or text widget needs to show without scrolling:
// the frame chrome (title row, borders, body padding) plus the natural content height.
// Layout px (offsetHeight), so the canvas zoom transform does not change it.
export function widgetContentNeed(frame) {
  return widgetContentFit(frame).need
}

// The content need plus, for a table, its whole-row stops: stops[k - 1] is the frame height at
// which exactly k body rows show without a cut (fitPageLayout uses them to show the most whole rows).
export function widgetContentFit(frame) {
  const body = frame?.querySelector(".datox-react-dashboard__widget-body")
  if (!body) return { need: 0 }
  const style = getComputedStyle(body)
  const chrome =
    frame.offsetHeight - body.clientHeight + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0)
  const container = body.querySelector(".datox-react-dashboard__table-container")
  // A squeezed table (single-line rows, see TableWidget) is not its natural content: measure it
  // unsqueezed (synchronously, so nothing paints and the table ends at the same size).
  if (container?.hasAttribute("data-squeeze")) {
    container.removeAttribute("data-squeeze")
    try {
      return widgetContentFit(frame)
    } finally {
      container.setAttribute("data-squeeze", "")
    }
  }
  if (container) {
    let others = 0
    let table = 0
    let viewport
    ;[...container.children].forEach((child) => {
      if (child.classList.contains("datox-react-dashboard__table-viewport")) {
        viewport = child
        // The table's natural height, never the viewport's clipped one, which follows the frame (§34: the fit must
        // not hide rows), so only the viewport's children count.
        table = [...child.children].reduce((inner, item) => inner + Math.max(item.offsetHeight, item.scrollHeight || 0), 0)
      } else others += child.offsetHeight
    })
    const content = others + table
    const bodyRows = [...(viewport?.querySelector("table")?.tBodies?.[0]?.rows || viewport?.querySelectorAll("table > tbody > tr") || [])]
    const stops = bodyRows.map((row) => Math.ceil(chrome + others + row.offsetTop + row.offsetHeight))
    return content > 0 ? { need: Math.ceil(chrome + content + 2), stops: stops.length ? stops : undefined } : { need: 0 }
  }
  const text = body.querySelector(".datox-react-dashboard__editable-text > span")
  const natural = text ? Math.max(text.offsetHeight, text.scrollHeight || 0) : 0
  return { need: natural ? Math.ceil(chrome + natural + 2) : 0 }
}

// The baseline strut of a KPI row: the largest value size in it. Each KPI keeps its own size
// (no shared size, no automatic compact notation); the strut only puts every value on one baseline.
export function kpiRowSize(fits) {
  const sizes = fits.filter(Boolean).map((fit) => fit.full).filter((size) => size > 0)
  return sizes.length ? Math.max(...sizes) : undefined
}
